import { useFieldWiring } from "./Field";
import { inputSurfaceVariants } from "./TextField";
import { cn } from "@/lib/cn";
import { CaretDown } from "@/icons";
import type { VariantProps } from "class-variance-authority";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";

type SurfaceProps = VariantProps<typeof inputSurfaceVariants>;

const CARET = "pointer-events-none h-4 w-4 shrink-0 text-card-text/50";

export type SelectProps = SurfaceProps &
  Omit<ComponentPropsWithoutRef<"select">, "size"> & {
    /** Applied to the positioning wrapper (layout only). */
    className?: string;
  };

/** Native `<select>` on the field surface with a caret; options come as `<option>` children. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, inputSize, className, children, ...rest },
  ref,
) {
  const { invalid: resolvedInvalid, ...wiring } = useFieldWiring({
    id: rest.id,
    "aria-describedby": rest["aria-describedby"],
    invalid,
    required: rest.required,
  });
  return (
    <div className={cn("relative w-full", className)}>
      <select
        {...rest}
        {...wiring}
        ref={ref}
        className={cn(
          inputSurfaceVariants({ invalid: resolvedInvalid, inputSize }),
          "cursor-pointer appearance-none pr-10 disabled:cursor-not-allowed",
        )}
      >
        {children}
      </select>
      <CaretDown aria-hidden weight="bold" className={cn(CARET, "absolute right-4 top-1/2 -translate-y-1/2")} />
    </div>
  );
});

export type SelectTriggerProps = SurfaceProps &
  Omit<ComponentPropsWithoutRef<"button">, "children" | "value"> & {
    /** The chosen option's label; the placeholder shows while empty. */
    value?: ReactNode;
    placeholder: ReactNode;
  };

/** Button drawn as a Select, for pickers that open a sheet or dialog (`Dialog.Trigger asChild`). */
export const SelectTrigger = forwardRef<HTMLButtonElement, SelectTriggerProps>(function SelectTrigger(
  { invalid, inputSize, value, placeholder, className, type = "button", ...rest },
  ref,
) {
  const { invalid: resolvedInvalid, "aria-required": _ariaRequired, ...wiring } = useFieldWiring({
    id: rest.id,
    "aria-describedby": rest["aria-describedby"],
    invalid,
  });
  const empty = value === undefined || value === null || value === "";
  return (
    <button
      {...rest}
      {...wiring}
      ref={ref}
      type={type}
      className={cn(
        inputSurfaceVariants({ invalid: resolvedInvalid, inputSize }),
        "flex items-center justify-between gap-2 text-left focus-visible:border-primary disabled:cursor-not-allowed",
        className,
      )}
    >
      <span className={cn("truncate", empty && "text-card-text/30")}>{empty ? placeholder : value}</span>
      <CaretDown aria-hidden weight="bold" className={CARET} />
    </button>
  );
});
